import { createEl, clearChildren } from '../dom.js';
import { highlightInto } from './highlight.js';

/**
 * Форматирует целочисленные копейки в человекочитаемую RU-строку цены.
 * 12345 копеек → "123 ₽" (копейки отбрасываются; конвенция российской торговли).
 */
function formatPrice(kopecks, currency) {
    if (typeof kopecks !== 'number') return '';
    const rubles = Math.round(kopecks / 100);
    const formatted = rubles.toLocaleString('ru-RU');
    return currency === 'USD' ? `$${formatted}` : `${formatted} ₽`;
}

/**
 * Dropdown — overlay-контейнер с секцией suggestions + секцией hits.
 *
 * Каждый вызов render(state) очищает+пересобирает. Форма state:
 *   { suggestions: Array<{text, score}>, hits: Array<{id, source}>,
 *     activeIndex, loading, error, query, total }
 *
 * activeIndex указывает в объединённый список [...suggestions, ...hits].
 *
 * Клик по suggestion → onSelectSuggestion(text).
 * Клик по hit → onSelectHit(hit, position).
 */
export class Dropdown {
    /**
     * @param {Element} container — .doka-root из SearchBox
     * @param {{
     *     onSelectSuggestion: (text: string) => void,
     *     onSelectHit: (hit: object, position: number) => void,
     *     renderHit?: (hit: object, defaultEl: Element) => Element|null|undefined
     * }} handlers
     */
    constructor(container, { onSelectSuggestion, onSelectHit, renderHit }) {
        this.handlers = { onSelectSuggestion, onSelectHit, renderHit };
        this.el = createEl('div', {
            className: 'doka-dropdown',
            id: `doka-dropdown-${Math.random().toString(36).slice(2, 8)}`,
            role: 'listbox',
        });
        container.appendChild(this.el);

        // Предотвращаем выделение текста при быстрых кликах
        this.el.addEventListener('mousedown', (e) => e.preventDefault());
    }

    open() {
        this.el.classList.add('is-open');
    }

    close() {
        this.el.classList.remove('is-open');
    }

    isOpen() {
        return this.el.classList.contains('is-open');
    }

    elementId() {
        return this.el.id;
    }

    /**
     * @param {Element} container контейнер .doka-root; используется для определения outside-click.
     * @param {() => void} onOutside
     * @returns {() => void} функция снятия listener'а для cleanup
     */
    bindOutsideClick(container, onOutside) {
        const handler = (e) => {
            if (!container.contains(e.target)) onOutside();
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }

    render(state) {
        clearChildren(this.el);

        if (state.loading) {
            this.el.appendChild(createEl('div', { className: 'doka-loading' }, ['Ищем…']));
            return;
        }

        if (state.error) {
            this.el.appendChild(createEl('div', { className: 'doka-error' }, ['Поиск временно недоступен']));
            return;
        }

        const hasSuggestions = state.suggestions.length > 0;
        const hasHits = state.hits.length > 0;

        if (!hasSuggestions && !hasHits) {
            this.el.appendChild(createEl('div', { className: 'doka-empty' }, ['Ничего не найдено']));
            return;
        }

        let runningIndex = 0;

        if (hasSuggestions) {
            const section = createEl('div', { className: 'doka-section' });
            section.appendChild(createEl('div', { className: 'doka-section-title' }, ['Подсказки']));

            for (const sug of state.suggestions) {
                const i = runningIndex;
                const isActive = i === state.activeIndex;
                const row = createEl('div', {
                    className: 'doka-suggest' + (isActive ? ' is-active' : ''),
                    id: `${this.el.id}-item-${i}`,
                    role: 'option',
                    'aria-selected': isActive ? 'true' : 'false',
                    onClick: () => this.handlers.onSelectSuggestion(sug.text),
                });
                highlightInto(row, sug.text, state.query);
                section.appendChild(row);
                runningIndex++;
            }
            this.el.appendChild(section);
        }

        if (hasHits) {
            const section = createEl('div', { className: 'doka-section' });
            section.appendChild(createEl('div', { className: 'doka-section-title' }, ['Товары']));

            state.hits.forEach((hit, hitIndex) => {
                const i = runningIndex;
                const isActive = i === state.activeIndex;
                const src = hit.source || {};
                const name = String(src.name ?? hit.id ?? '');
                const imageUrl = typeof src.image_url === 'string' ? src.image_url : null;
                const price = typeof src.price_amount === 'number'
                    ? formatPrice(src.price_amount, src.price_currency)
                    : '';
                const url = typeof src.product_url === 'string' ? src.product_url : null;

                const title = createEl('div', { className: 'doka-hit-title' });
                highlightInto(title, name, state.query);

                const meta = createEl('div', { className: 'doka-hit-meta' }, [
                    src.brand ? createEl('span', { className: 'doka-hit-brand' }, [String(src.brand)]) : null,
                    price ? createEl('span', { className: 'doka-hit-price' }, [price]) : null,
                ]);

                const thumb = imageUrl
                    ? createEl('img', { className: 'doka-hit-thumb', src: imageUrl, alt: '', loading: 'lazy' })
                    : createEl('div', { className: 'doka-hit-thumb doka-hit-thumb--placeholder' }, [
                        (name.charAt(0) || '?').toUpperCase(),
                    ]);

                const onSelectThisHit = () => this.handlers.onSelectHit(hit, hitIndex + 1);

                const defaultEl = createEl(url ? 'a' : 'div', {
                    className: 'doka-hit' + (isActive ? ' is-active' : '') + (url ? '' : ' doka-hit--no-url'),
                    id: `${this.el.id}-item-${i}`,
                    role: 'option',
                    'aria-selected': isActive ? 'true' : 'false',
                    dataset: { hitIndex: String(hitIndex) },
                    ...(url ? { href: url, onClick: (e) => {
                        e.preventDefault();
                        onSelectThisHit();
                    } } : {}),
                }, [thumb, createEl('div', { className: 'doka-hit-body' }, [title, meta])]);

                let finalEl = defaultEl;
                if (typeof this.handlers.renderHit === 'function') {
                    // Передаём hit с колбэком `onSelect`, чтобы кастомные рендеры могли
                    // подключить свою цель клика, не трогая внутренности.
                    const hitView = Object.assign({}, hit, { onSelect: onSelectThisHit });
                    try {
                        const ret = this.handlers.renderHit(hitView, defaultEl);
                        if (ret instanceof Element) {
                            finalEl = ret;
                        } else if (ret !== null && ret !== undefined) {
                            console.warn('[doka] renderHit returned non-Element; falling back to default');
                        }
                    } catch (e) {
                        console.warn('[doka] renderHit threw; falling back to default', e);
                    }
                }

                section.appendChild(finalEl);
                runningIndex++;
            });

            this.el.appendChild(section);
        }

        // Опциональный footer: "+N ещё на сайте", когда total превышает отрендеренные hits.
        if (state.total > state.hits.length) {
            const more = state.total - state.hits.length;
            this.el.appendChild(createEl('div', { className: 'doka-footer' }, [
                `+${more} результатов на сайте`,
            ]));
        }

        // BL-H1 — атрибуция brand-badge. Тенанты Free + Starter/Growth
        // рендерят "powered by dokasearch"; Pro / Enterprise её скрывают.
        // Дефолтное значение state — `mandatory`, чтобы любой сбой config-fetch
        // всё равно показывал атрибуцию (более безопасный default).
        if (state.brandBadge && state.brandBadge !== 'hidden') {
            const badge = createEl('a', {
                className: 'doka-brand-badge',
                href: 'https://dokasearch.ru/?utm_source=widget&utm_medium=brand_badge',
                target: '_blank',
                rel: 'noopener noreferrer nofollow',
            }, ['Поиск работает на dokasearch']);
            this.el.appendChild(badge);
        }
    }
}
